import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../UserRole/CreateUser.css';
import Select from "react-select";
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const CreateUserPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userData = location.state || {};
  const token = localStorage.getItem('auth-token');
  const isEditMode = !!userData.userId;
  const [name, setName] = useState(userData.name || '');
  const [email, setEmail] = useState(userData.email || '');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState(isEditMode ? userData.userType : '');
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [selectedAcademy, setSelectedAcademy] = useState(
    (userData.academyIds || []).map((id) => {
      const academy = academies.find((a) => String(a.academyId) === String(id));
      return academy ? { value: academy.academyId, label: academy.academyName } : null;
    }).filter(Boolean)
  );  
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);

  const userTypeOptions = [
    { value: 'ADMIN', label: 'ADMIN' },
    { value: 'ACADEMY', label: 'ACADEMY' },
  ];
  const selectedUserTypeOption = userTypeOptions.find(opt => opt.value === userType) || null;

  const normalizePayload = (payload) => {
    return {
      ...payload,
      academyIds: (payload.academyIds || []).slice().sort(),
      userRoles: (payload.userRoles || []).slice().sort((a, b) => a.id - b.id),
    };
  };

  const buildSubmitPayload = () => {
    return {
      id: userData?.userId,
      userType: userType || '',
      name: name || '',
      email: email || '',
      password: password || (isEditMode ? userData?.password : ''),
      userRoles: selectedRoles.map((role) => ({
        id: role.id,
        roleName: role.roleName,
      })),
      academyIds: (selectedAcademy || []).map((academy) => academy.value),
    };
  };

  const redactForAudit = (payload) => {
    if (!payload) return payload;
    const copy = { ...payload };
    if (typeof copy.password !== 'undefined' && copy.password !== '') {
      copy.password = '***';
    }
    return copy;
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_API_ROLE_LIST, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const responseData = await response.json();
          const rolesMapped = responseData.data.roles.map((role) => ({
            id: role.id,
            roleName: role.roleName,
          }));
          setRoles(rolesMapped);

          const academiesMapped = Object
            .entries(responseData.data.academies)
            .map(([academyId, academyName]) => ({ academyId, academyName }));
          setAcademies(academiesMapped);

          if (isEditMode) {
            const selected = responseData.data.roles
              .filter((role) =>
                (userData.roles || []).some(
                  (userRole) =>
                    (typeof userRole === 'string' && userRole === role.roleName) ||
                    (typeof userRole === 'object' && userRole.roleName === role.roleName)
                )
              )
              .map(role => ({ id: role.id, roleName: role.roleName }));
            setSelectedRoles(selected);
          }
        }
      } catch (error) {
        console.log(error)
      }
    };

    fetchRoles();
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode && userData.academyIds && academies.length) {
      const selectedAcademies = userData.academyIds
        .map((academyId) => {
          const academy = academies.find((a) => String(a.academyId) === String(academyId));
          return academy ? { value: academy.academyId, label: academy.academyName } : null;
        })
        .filter(Boolean);
      setSelectedAcademy(selectedAcademies);
    }
  }, [isEditMode, userData.academyIds, academies]);

  useEffect(() => {
    if (isEditMode && userData.userType) {
      setUserType(userData.userType);
    }
  }, [isEditMode, userData.userType]);

  useEffect(() => {
    if (initializedBaselineRef.current) return;
    const readyForBaseline = isEditMode
      ? roles.length > 0 &&
        academies.length > 0 &&
        (selectedRoles.length > 0 || (userData.roles || []).length === 0)
      : true;
  
    if (readyForBaseline) {
      const basePayload = buildSubmitPayload();
      basePayload.academyIds = userData.academyIds || [];

      baselineRef.current = normalizePayload(basePayload);
      initializedBaselineRef.current = true;
    }
  }, [isEditMode, roles.length, selectedRoles.length, academies.length, name, email, userType]);

  const handleRoleSelectChange = (options) => {
    const selected = options ? options.map(opt => ({ id: opt.value, roleName: opt.label })) : [];
    setSelectedRoles(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    if (userType === 'ACADEMY' && (!selectedAcademy || selectedAcademy.length === 0)) {
      setError('Please select a center. The center cannot be null for Academy user type.');
      setIsSubmitting(false);
      return;
    }

    const apiUrlBase = process.env.REACT_APP_API_NEW_USER_ROLE;
    const apiUrl = userData?.userId ? `${apiUrlBase}?userId=${userData.userId}` : apiUrlBase;

    const newPayload = normalizePayload(buildSubmitPayload());
    const oldPayload = baselineRef.current || {};

    if (isEditMode) {
      const { digest, html } = buildDiffPreview(redactForAudit(oldPayload), redactForAudit(newPayload));
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500,
        });
        setIsSubmitting(false);
        return;
      }

      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) {
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newPayload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        fireAudit({
          entity: 'user',
          affected_id: userData?.userId || null,
          action: isEditMode ? 'update' : 'create',
          businessApi: apiUrl,
          businessMethod: 'POST',
          oldPayload: redactForAudit(oldPayload),
          newPayload: redactForAudit(newPayload),
          section: 'UserRole',
          subSection: 'CreateUser',
          extra: { page: 'CreateUserPage' },
        });
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: isEditMode ? 'User updated successfully' : 'User created successfully',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
        baselineRef.current = newPayload;

        navigate('/UserList');
      } else {
        const errorMessage =
          result.success_message || result.data?.errorMessage || 'An error occurred';
        setError(errorMessage);
      }
    } catch (err) {
      setError('Unable to connect to the server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/UserList" className="breadcrumb-link">User List</Link> &gt; {isEditMode ? 'Edit User' : 'New User'}
      </div>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit} autoComplete="off">
        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            name="userType"
            options={userTypeOptions}
            value={selectedUserTypeOption}
            onChange={(selectedOption) => setUserType(selectedOption?.value || '')}
            placeholder="Select User Type"
            isClearable
            className={`walkin-form-dropdown ${userType ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {userType && <label htmlFor="userType">User Type</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            name="academy"
            isMulti
            options={academies.map((academy) => ({
              value: academy.academyId,
              label: academy.academyName,
            }))}
            value={selectedAcademy}
            onChange={setSelectedAcademy}
            isDisabled={userType !== 'ACADEMY'}
            placeholder="Select Academy"
            closeMenuOnSelect={false}
            className={`walkin-form-dropdown ${selectedAcademy?.length ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            isClearable
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {selectedAcademy?.length > 0 && <label htmlFor="academy">Academy</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="text"
            name="name"
            className={`walkin-form ${name ? 'has-value' : ''}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter name"
            required
          />
          {name && <label htmlFor="name">Name</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="email"
            name="email"
            className={`walkin-form ${email ? 'has-value' : ''}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email"
            required
          />
          {email && <label htmlFor="email">Email</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="password"
            name="password"
            className={`walkin-form ${password ? 'has-value' : ''}`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            autoComplete="new-password"
          />
          {password && <label htmlFor="password">Password</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            name="roles"
            isMulti
            options={roles.map(role => ({ value: role.id, label: role.roleName }))}
            value={selectedRoles.map(role => ({ value: role.id, label: role.roleName }))}
            onChange={handleRoleSelectChange}
            closeMenuOnSelect={false}
            placeholder="Select Roles"
            menuPortalTarget={document.body}
            className={`walkin-form-dropdown ${selectedRoles.length ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            styles={{
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              control: (provided) => ({ ...provided, padding: '5px' }),
            }}
          />
          {selectedRoles.length > 0 && <label htmlFor="roles">Roles</label>}
        </div>

        <button type="submit" className="submit-button" disabled={isSubmitting}>
          {isSubmitting ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create User')}
        </button>
      </form>
    </div>
  );
};

export default CreateUserPage;